import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Formsy, { withFormsy } from 'formsy-react'; // Import withFormsy

import ActionButton from 'SharedComponents/action-button';
import TextInput from 'SharedComponents/text-input';
import DropDownSelect from 'SharedComponents/dropdown-select';
import PopoverHover from 'SharedComponents/popover-hover';

import useUpdateContactDetails from 'SharedHooks/update-contact-details';
import useAutocompleteAddress from 'SharedHooks/auto-complete-address';

import log from "Utils/logger";

import styles from './index.css.js';
import { backIn } from "framer-motion";


const ContactAndVerifyFormWrapper = styled.section`${styles}`;


const TextInputWithFormsy = withFormsy(TextInput);

const ContactAndVerifyForm = ({ fieldsToHide = [] }) => {
    
  const user = useSelector(state => state.authentication.user);
  const identity = useSelector(state => state.authentication.identity);

  const hideField = (field) => fieldsToHide.includes(field);

  console.log('ContactAndVerifyForm user from redux state: ', user);
  
  //const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  
  const [fname, setFname] = useState(identity?.shopper_fname || '');
  const [lname, setLname] = useState(identity?.shopper_lname || '');
  const [fullName, setFullname] = useState(
    `${identity?.shopper_fname || ''} ${identity?.shopper_lname || ''}`.trim()
  );

  const [address_street, setAddressStreet] = useState(identity?.address_street || '');
  const [address_suburb, setAddressSuburb] = useState(identity?.address_suburb || '');
  const [address_state, setAddressState] = useState(identity?.address_state || '');
  const [address_postcode, setAddressPostcode] = useState(identity?.address_postcode || '');
  const [formatted_address, setFormattedAddress] = useState(identity?.formatted_address || '');


  const [email, setEmail] = useState(identity?.shopper_email || '');
  const [phone, setPhone] = useState(identity?.shopper_phone || '');
  const [dob, setDob] = useState(identity?.shopper_dob || '');

  const [isFormValid, setIsFormValid] = useState(false);

  const rawInputAddressRef = useRef(null);
  
  const { components, rawInput, setRawInput, autoCompleteError, handleChange } = useAutocompleteAddress(rawInputAddressRef);

  const politicallyExposedOptions = [
    { value: null, label: 'Not Specified' },
    { value: true, label : 'Yes' },
    { value: false, label : 'No' },
  ]

  const [pep, setPep] = useState(() => {
    const defaultValue = politicallyExposedOptions[0].value;
    return identity?.shopper_pep || defaultValue;
  });

  useEffect(() => {
  
      console.log('ContactAndVerifyForm - mounted');

      return (() => {
          console.log('ContactAndVerifyForm - unmounted');
      });
  }, []);


  useEffect(() => {
    if (identity?.formatted_address) {
        setRawInput(formatted_address);
    }
    setEmail(identity?.shopper_email || ''); 
    setPhone(identity?.shopper_phone || ''); 
    setDob(identity?.shopper_dob || ''); 
    setFname(identity?.shopper_fname || '');
    setLname(identity?.shopper_lname || '');
    setFullname(`${identity?.shopper_fname || ''} ${identity?.shopper_lname || ''}`.trim());
    setPep(identity?.shopper_pep);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    
    const currentAddress = [
      components.address_street,
      components.address_suburb,
      components.address_state,
      components.address_postcode,
    ].filter(Boolean).join(", ") || rawInput;

    if (currentAddress && currentAddress !== formatted_address) {
      setFormChanged(true);
    }
  }, [rawInput, components]);
 
  const updateEmail = (e) => {
    setEmail(e.target.value);
    setFormChanged(true);
  }

  const updatePhone = (e) => {
    setPhone(e.target.value);
    setFormChanged(true);
  }

  const updateDob = (e) => {
    setDob(e.target.value);
    setFormChanged(true);
  };

  const updateName = (e) => {
    const fullName = e.target.value;
    setFullname(fullName);
    setFormChanged(true);
    // Split only on first space
    const [first, ...rest] = fullName.trim().split(" ");
    setFname(first || "");
    setLname(rest.join(" ") || "");
  };

  const updatePep = (option) => {
    setPep(option.value);
    setFormChanged(true);
  };
    
  const { updateContactDetails } = useUpdateContactDetails();

  const handleClickUpdateContactInfo = async () => {

    setIsSubmitting(true);
    setMessage("");

    try {
      const finalComponents = Object.keys(components).length > 0 
      ? components 
      : {
          address_street,
          address_suburb,
          address_state,
          address_postcode,
        };

      const payload = {
        shopper_fname: fname,
        shopper_lname: lname,
        shopper_phone: phone,
        shopper_email: email,
        shopper_dob: dob,
        shopper_pep: pep,
        ...finalComponents,
      };

      await updateContactDetails( payload );

      setMessage("Contact information updated successfully.");
    } catch (error) {
      console.error("Update contact info failed:", error);
      setMessage("Failed to update contact info. Please try again.");
    } finally {
      setIsSubmitting(false);
      setFormChanged(false);
    }
  };

  return (
    <ContactAndVerifyFormWrapper>
      <Formsy
          style={{ width: 'calc(100% - 20px)', padding: '0px 10px' }}
          onValid={() => setIsFormValid(true)}
          onInvalid={() => setIsFormValid(false)}
      >
        <div className="input-fields-container">
          {!hideField('name') && (
            <TextInputWithFormsy
                name="full-name"
                id="shopper-name"
                type="text"
                validations={{
                    isExisty: true,
                    minLength: 2,
                    maxLength: 50,
                    matchRegexp: /^[a-zA-Z'-]+\s+[a-zA-Z\s'-]+$/, 
                }}
                validationErrors={{
                    isExisty: "This field is required",
                    minLength: "Name must be at least 2 characters",
                    maxLength: "Name must be less than 60 characters",
                    matchRegexp: "Not accepted format, enter both first and last name",
                }}
                required
                value={fullName}
                onChange={updateName}
                placeholder="Firstname Lastname"
                style={{fontSize: "14px", color: "#000", fontFamily: "inherit"}}
                labelStyle={{ margin: '0 0 -5px 0' }}
                label={
                <div className='input-label-with-info'>
                    Legal Name* {/* <span className="tooltip-container"><div className="tooltip-icon">i</div><span className="tooltip"><div className="content">You can use the following as a guide: Cash = 2.7%, Conservative=3.8%, Moderate = 4.4%, Balanced = 4.8%, Growth = 5%, High Growth = 5.3%</div></span></span></div> */}
                    <span className="pop-over-container">
                        <PopoverHover
                            content={
                                <div style={{
                                maxWidth: '300px', padding: 10, fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', textAlign: 'left',
                                }}
                                >
                                You must use your legal name as it appears on your ID or passport. It will be verified before you can use Open Banking for round ups.
                                </div>
                            }
                            header = {null}
                            img = {null}
                            itemToPopover = {
                                ( <i className="material-icons" style={{ margin: '0 5px 0 5px', fontSize:'20px', color:'#000'}} >info</i> )
                            }
                            anchorOrigin={{
                                vertical: 'center',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'center',
                                horizontal: 'right',
                            }}
                        />
                    </span>
                </div>
                }
            />
          )}
          {!hideField('email') && user.signup_method === 'email' && (
            <TextInputWithFormsy
                name="Email"
                id="email"
                type="text"
                validations={{ 
                isEmail: true,
                isExisty: true,
                matchRegexp: /^(((?!(info@|admin@|web@|support@)).)*)$/,
                }}
                validationErrors={{
                isEmail: 'the email is invalid',
                matchRegexp: 'some role based emails are disallowed including admin@, info@ and web@',
                isDefaultRequiredValue: 'this field is required',
                isExisty: 'this field is required'
                }}
                required
                value={email}
                onChange={updateEmail}
                placeholder="email"
                style={{fontSize: "14px", color: "#000", fontFamily: "inherit"}}
                labelStyle={{ margin: '0 0 -5px 0' }}
                label={
                <div className='input-label-with-info'>
                    Enter your email {/* <span className="tooltip-container"><div className="tooltip-icon">i</div><span className="tooltip"><div className="content">You can use the following as a guide: Cash = 2.7%, Conservative=3.8%, Moderate = 4.4%, Balanced = 4.8%, Growth = 5%, High Growth = 5.3%</div></span></span></div> */}
                    <span className="pop-over-container">
                    <PopoverHover
                    content={
                        <div style={{
                        maxWidth: '300px', padding: 10, fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', textAlign: 'left',
                        }}
                        >
                        Enter your email
                        </div>
                    }
                    header = {null}
                    img = {null}
                    itemToPopover = {
                        ( <i className="material-icons" style={{ margin: '0 5px 0 5px', fontSize:'20px', color:'#000'}} >info</i> )
                    }
                    anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'center',
                        horizontal: 'right',
                    }}
                    />
                </span>
                </div>
                }
            />
          )}
          {/* email with confirmation from below goes here */}

          {!hideField('phone') && (
            <TextInputWithFormsy
                name="phone"
                id="phone"
                type="tel"
                validations={{ 
                    isExisty: true,
                    minLength: 10,
                    maxLength: 16,
                    matchRegexp: /^\+{0,1}(?:[0-9] ?){6,14}[0-9]$/
                }}
                validationErrors={{
                    isExisty: 'this field is required',
                    isDefaultRequiredValue: 'this field is required',
                    minLength: 'too short',
                    maxLength: 'too long',
                    matchRegexp: 'must be a valid phone number'
                }}   
                required
                value={phone}
                onChange={updatePhone}
                style={{fontSize: "14px", color: "#000", fontFamily: "inherit"}}
                labelStyle={{ margin: '0 0 -5px 0' }}
                label={
                <div className='input-label-with-info'>
                  Enter Your Phone {/* <span className="tooltip-container"><div className="tooltip-icon">i</div><span className="tooltip"><div className="content">You can use the following as a guide: Cash = 2.7%, Conservative=3.8%, Moderate = 4.4%, Balanced = 4.8%, Growth = 5%, High Growth = 5.3%</div></span></span></div> */}
                  <span className="pop-over-container">
                  <PopoverHover
                    content={
                      <div style={{
                      maxWidth: '300px', padding: 10, fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', textAlign: 'left',
                      }}
                      >
                      Your phone number will be confirmed.
                      </div>
                    }
                    header = {null}
                    img = {null}
                    itemToPopover = {
                      ( <i className="material-icons" style={{ margin: '0 5px 0 5px', fontSize:'20px', color:'#000'}} >info</i> )
                    }
                    anchorOrigin={{
                      vertical: 'center',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'center',
                      horizontal: 'right',
                    }}
                  />
                  </span>
                </div>
                }
              />
          )}
          {!hideField('dob') && (
            <TextInputWithFormsy
              name="dob"
              id="dob"
              type="date"
              validations={{
                isExisty: true,
                matchRegexp: /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/, // YYYY-MM-DD
              }}
              validationErrors={{
                isExisty: "This field is required",
                matchRegexp: "Date must be in format DD-MM-YYYY",
              }}
              required
              value={dob}
              onChange={updateDob}
              style={{fontSize: "14px", color: "#000", fontFamily: "inherit"}}
              labelStyle={{ margin: "0 0 -5px 0" }}
              label={
                <div className="input-label-with-info">
                  Date of Birth*
                  <span className="pop-over-container">
                    <PopoverHover
                      content={
                        <div
                          style={{
                            maxWidth: "300px",
                            padding: 10,
                            fontSize: "16px",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "flex-start",
                            flexDirection: "column",
                            textAlign: "left",
                          }}
                        >
                          Enter your date of birth. *Must match your Bank Account for Round-Ups to be turned on.
                        </div>
                      }
                      header={null}
                      img={null}
                      itemToPopover={
                        <i
                          className="material-icons"
                          style={{
                            margin: "0 5px 0 5px",
                            fontSize: "20px",
                            color: "#000",
                          }}
                        >
                          info
                        </i>
                      }
                      anchorOrigin={{
                        vertical: "center",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "center",
                        horizontal: "right",
                      }}
                    />
                  </span>
                </div>
              }
            />
          )}
          {!hideField('address') && (
              <TextInputWithFormsy
                  name="address"
                  id="address"
                  type="text"
                  validations={{
                      isExisty: true,
                      minLength: 5,
                      maxLength: 120,
                  }}
                  validationErrors={{
                      isExisty: "This field is required",
                      minLength: "Address must be at least 5 characters",
                      maxLength: "Address must be less than 120 characters",
                  }}
                  required
                  value={rawInput}
                  onChange={handleChange} // still tracks input
                  inputRef={rawInputAddressRef} // 👈 so we can attach Google Autocomplete
                  validationError={autoCompleteError}
                  placeholder="Enter your address"
                  style={{fontSize: "14px", color: "#000"}}
                  labelStyle={{ margin: "0 0 -5px 0" }}
                  label={
                      <div className="input-label-with-info">
                      Address*
                      <span className="pop-over-container">
                          <PopoverHover
                          content={
                              <div
                              style={{
                                  maxWidth: "300px",
                                  padding: 10,
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "flex-start",
                                  flexDirection: "column",
                                  textAlign: "left",
                              }}
                              >
                              Start typing your address and pick from the suggestions. *Must match your bank account address for Round-Ups to be turned on.
                              </div>
                          }
                          header={null}
                          img={null}
                          itemToPopover={
                              <i
                              className="material-icons"
                              style={{ margin: "0 5px 0 5px", fontSize: "20px", color: "#000" }}
                              >
                              info
                              </i>
                          }
                          anchorOrigin={{
                              vertical: "center",
                              horizontal: "left",
                          }}
                          transformOrigin={{
                              vertical: "center",
                              horizontal: "right",
                          }}
                          />
                      </span>
                      </div>
                  }
              />
          )}
          
          {!hideField('pep') && (
              <div className="title-and-limit">
                  <h2 className="title">Are you a Politically Exposed Person?*
                  <span className="pop-over-container">
                          <PopoverHover
                          content={
                              <div
                              style={{
                                  maxWidth: "300px",
                                  padding: 10,
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "flex-start",
                                  flexDirection: "column",
                                  textAlign: "left",
                              }}
                              >
                              AUSTRAC requires us to verify if you are a Politically Exposed Person (PEP). A PEP is someone who holds, or has held, a prominent public position — such as a government official, senior politician, judge, or a high ranking military officer such as a general.
                              </div>
                          }
                          header={null}
                          img={null}
                          itemToPopover={
                              <i
                              className="material-icons"
                              style={{ margin: "0 5px 0 5px", fontSize: "20px", color: "#000" }}
                              >
                              info
                              </i>
                          }
                          anchorOrigin={{
                              vertical: "center",
                              horizontal: "left",
                          }}
                          transformOrigin={{
                              vertical: "center",
                              horizontal: "right",
                          }}
                          />
                      </span>
                      </h2>
                  <div className="limit">
                  <DropDownSelect 
                      className="dropdown-menu"
                      value={pep} 
                      onChange={updatePep}
                      options={politicallyExposedOptions}
                      valueField="value" 
                      labelField="label"
                      style={{ width: '100%', backgroundColor: '#fff' }}
                  />
                  </div>
              </div>
          )}
          
        </div>
        <div className="action-button-update-holder">
            <ActionButton type="submit"
                text={isSubmitting ? 'Updating Verification Details' : 'Update Verification Details'}
                large={true}
                disabled={isSubmitting || !formChanged || !isFormValid || pep === null}    
                loading={isSubmitting}
                active={!isSubmitting}
                onClick={() => handleClickUpdateContactInfo()}
            />
        </div>
        
        
        {message && <p>{message}</p>}
      </Formsy>
    </ContactAndVerifyFormWrapper>
  );
};

export default ContactAndVerifyForm;

{/* //the following when we have email confirmation working.
    <div className="email-with-confirmation">
    <TextInputWithFormsy
        name="Your Email"
        id="shopper-email"
        type="email"
        validations={{
            isEmail: true, 
            isExisty: true,
            isDefaultRequiredValue: 'this field is required',
            minLength: 2,
            maxLength: 50,
            matchRegexp: /^(((?!(info@|admin@|web@|support@)).)*)$/,
        }}
        validationErrors={{
            isEmail: 'The email is invalid',
            isExisty: 'This field is required',
            isDefaultRequiredValue: 'this field is required',
            minLength: "Your email must be at least 2 characters",
            maxLength: "Your email must be less than 50 characters",
            matchRegexp: "Role based emails are disallowed",
        }}
        required
        value={email}
        onChange={updateEmail}
        //placeholder="First Name"
        labelStyle={{ margin: '0 0 -5px 0' }}
        label={
        <div className='input-label-with-info'>
            Enter your email
            <span className="pop-over-container">
            <PopoverHover
            content={
                <div style={{
                maxWidth: '300px', padding: 10, fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', textAlign: 'left',
                }}
                >
                Enter your email.
                </div>
            }
            header = {null}
            img = {null}
            itemToPopover = {
                ( <i className="material-icons" style={{ margin: '0 5px 0 5px', fontSize:'20px', color:'#000'}} >info</i> )
            }
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            />
        </span>
        </div>
        }
    />
    <div className="action-button-holder" style={{ width: "70px", padding: "12px 10px 0 10px"}}>
        <ActionButton
            text="Confirm Email"
            onClick={(e) => e.preventDefault()} //() =>  handleSubmit()}
            color="#fff"
            small={true}
            //disabled={!stripe || !clientSecret || loading}
        />
    </div>
</div> */}